import { DataUsage, ServiceLineStatus, ServiceLineType } from "@/shared/types/serviceLineTypes";
import { TerminalType } from "@/shared/types/userTerminalTypes";

export interface ServiceLinesSummary {
  statuses: { status: ServiceLineStatus; value: number; }[] | null;
  types: { type: ServiceLineType; value: number; }[] | null;
}

export interface TerminalsSummary {
  total: number;
  deployed: number;
  types: { type: TerminalType; total: number; deployed: number; }[] | null;
}

export interface UsageSummary {
  totalDataUsageGB: number | 0;
  totalStandardGB: number | 0;
  cappedForecastGB: number | 0;
  uncappedForecastGB: number | 0;
  contractedDatapool: number | 0;
  basePlan: number | 0;
}

/** One month row inside `OverviewData.summary`. */
export interface SummaryData {
  startDate: string;
  endDate: string;
  usageSummary?: UsageSummary;
  serviceLinesSummary?: ServiceLinesSummary;
  terminalsSummary?: TerminalsSummary;
}

/** Single account rollup (or one child row in parent multi-account responses). */
export interface OrganizationOverviewData {
  organizationId: string;
  name: string;
  summary: SummaryData[];
}

/** Build `DataUsage` from `SummaryData`. */
export function buildDataUsage(summary: SummaryData): DataUsage {
  return {
    startDate: summary.startDate,
    endDate: summary.endDate,
    totalDataUsageGB: summary.usageSummary.totalDataUsageGB,
    totalStandardGB: summary.usageSummary.totalStandardGB,
    cappedForecastGB: summary.usageSummary.cappedForecastGB,
    uncappedForecastGB: summary.usageSummary.uncappedForecastGB,
    contractedDatapoolGB: summary.usageSummary.contractedDatapool,
    basePlanGB: summary.usageSummary.basePlan,
    dailyDataUsages: null,
  };
}

export function getBreakdownOfServiceLineStatuses(serviceLinesSummary: ServiceLinesSummary | null | undefined): Record<ServiceLineStatus, number> | undefined {
  if (!serviceLinesSummary?.statuses?.length) return undefined;
  const statuses = serviceLinesSummary?.statuses;
  return {
    [ServiceLineStatus.ACTIVE]: statuses?.find((s) => s.status === ServiceLineStatus.ACTIVE)?.value ?? 0,
    [ServiceLineStatus.EXHAUSTED]: statuses?.find((s) => s.status === ServiceLineStatus.EXHAUSTED)?.value ?? 0,
    [ServiceLineStatus.PAUSED]: statuses?.find((s) => s.status === ServiceLineStatus.PAUSED)?.value ?? 0,
    [ServiceLineStatus.NO_UT]: statuses?.find((s) => s.status === ServiceLineStatus.NO_UT)?.value ?? 0,
    [ServiceLineStatus.OFFLINE]: statuses?.find((s) => s.status === ServiceLineStatus.OFFLINE)?.value ?? 0,
    [ServiceLineStatus.SUSPENDED]: statuses?.find((s) => s.status === ServiceLineStatus.SUSPENDED)?.value ?? 0,
  } as Record<ServiceLineStatus, number>;
}